<template>
  <div>
    <div class="message-template__label-row">
      <label class="d-block mb-0">
        {{ $t(config.label) }}
        <span class="ml-50"
          >{{ chartsCount }}
          {{ $t("commands.form.message_template.characters_remaining") }}</span
        >
      </label>
      <button
        v-if="!isWebhook"
        type="button"
        class="btn btn-sm btn-outline-secondary message-template__placeholder-button"
        @mousedown.prevent
        @click="togglePlaceholderPicker"
      >
        <feather-icon icon="PlusIcon" size="14" />
        {{ $t("commands.form.message_template.insert_placeholder") }}
      </button>
    </div>
    <validation-provider
      :name="$t(config.name)"
      :rules="config.rules"
      v-slot="{ errors }"
    >
      <div class="message-template__field">
        <textarea
          ref="templateInput"
          v-model="model"
          class="form-control message-template__input"
          :class="{ 'message-template__input--webhook': isWebhook }"
          :rows="isWebhook ? 7 : rows"
          :readonly="isWebhook"
          :maxlength="maxChars"
          @input="onTemplateInput"
          @click="onTemplateInteraction"
          @keyup.left="onTemplateInteraction"
          @keyup.right="onTemplateInteraction"
          @keyup.home="onTemplateInteraction"
          @keyup.end="onTemplateInteraction"
          @keydown.down="moveInlineSuggestion($event, 1)"
          @keydown.up="moveInlineSuggestion($event, -1)"
          @keydown.enter="selectActiveInlineSuggestion"
          @keydown.esc="hideInlinePlaceholderSuggestions"
          @blur="hideInlinePlaceholderSuggestions"
        ></textarea>
        <div
          v-if="filteredInlinePlaceholderSuggestions.length"
          class="message-template__suggestions"
        >
          <button
            v-for="(placeholder, index) in filteredInlinePlaceholderSuggestions"
            :key="placeholder.placeholder"
            type="button"
            class="message-template__suggestion"
            :class="{ 'message-template__suggestion--active': index === inlineActiveSuggestionIndex }"
            @mousedown.prevent="insertInlinePlaceholder(placeholder.placeholder, true)"
          >
            <code>{{ placeholder.placeholder }}</code>
            <small>{{ placeholder.description }}</small>
          </button>
        </div>
      </div>
      <button
        v-if="isWebhook"
        type="button"
        class="btn btn-sm btn-outline-secondary mt-1"
        @click="openEditor"
      >
        {{ $t('commands.form.json_editor.expand') }}
      </button>
      <p v-if="config.hint" class="mb-0 small">{{ $t(config.hint) }}</p>
      <p v-if="errors" class="small text-danger">{{ errors[0] }}</p>
    </validation-provider>
    <p v-if="!isWebhook" class="mb-0 mt-50 small text-muted">
      {{ $t("commands.form.message_template.placeholder_hint") }}
    </p>
    <div v-if="placeholderPickerOpen" class="message-template__placeholder-picker">
      <input
        ref="placeholderSearch"
        v-model="placeholderSearch"
        type="search"
        class="form-control form-control-sm mb-75"
        :placeholder="$t('commands.form.message_template.search_placeholder')"
      />
      <div
        v-for="group in filteredPlaceholderGroups"
        :key="group.key"
        class="message-template__placeholder-group"
      >
        <p class="message-template__placeholder-group-title">{{ group.label }}</p>
        <button
          v-for="placeholder in group.placeholders"
          :key="placeholder.placeholder"
          type="button"
          class="message-template__placeholder-option"
          @mousedown.prevent="insertInlinePlaceholder(placeholder.placeholder, inlinePlaceholderQuery !== null)"
        >
          <code>{{ placeholder.placeholder }}</code>
          <span>{{ placeholder.description }}</span>
        </button>
      </div>
      <p v-if="!filteredPlaceholderGroups.length" class="mb-0 small text-muted">
        {{ $t("commands.form.message_template.no_placeholders_found") }}
      </p>
    </div>

    <div v-if="editorOpen" class="webhook-editor" role="dialog" aria-modal="true">
      <header class="webhook-editor__header">
        <div>
          <h3 class="webhook-editor__title">
            {{ $t('commands.form.json_editor.title') }}
          </h3>
          <small :class="jsonError ? 'text-danger' : 'text-success'">
            {{ jsonError || $t('commands.form.json_editor.valid') }}
          </small>
        </div>
        <button
          type="button"
          class="webhook-editor__close"
          :aria-label="$t('commands.form.json_editor.cancel')"
          @click="closeEditor"
        >
          &times;
        </button>
      </header>

      <main class="webhook-editor__body">
        <textarea
          ref="jsonEditor"
          v-model="editorDraft"
          class="webhook-editor__textarea"
          :maxlength="maxChars"
          spellcheck="false"
          @input="updatePlaceholderSuggestions"
          @click="updatePlaceholderSuggestions"
          @keyup.left="updatePlaceholderSuggestions"
          @keyup.right="updatePlaceholderSuggestions"
          @keydown.tab.prevent="insertIndent"
          @keydown.down="moveSuggestion($event, 1)"
          @keydown.up="moveSuggestion($event, -1)"
          @keydown.enter="selectActiveSuggestion"
          @keydown.esc.stop="hidePlaceholderSuggestions"
        ></textarea>
        <div
          v-if="filteredPlaceholderSuggestions.length"
          class="webhook-editor__suggestions"
        >
          <button
            v-for="(placeholder, index) in filteredPlaceholderSuggestions"
            :key="placeholder.placeholder"
            type="button"
            class="webhook-editor__suggestion"
            :class="{ 'webhook-editor__suggestion--active': index === activeSuggestionIndex }"
            @mousedown.prevent="insertPlaceholder(placeholder.placeholder)"
          >
            <strong>{{ placeholder.placeholder }}</strong>
            <small>{{ placeholder.description }}</small>
          </button>
        </div>
      </main>

      <footer class="webhook-editor__footer">
        <span class="webhook-editor__counter">
          {{ editorCharsCount }} {{ $t('commands.form.message_template.characters_remaining') }}
        </span>
        <button type="button" class="btn btn-outline-secondary" @click="closeEditor">
          {{ $t('commands.form.json_editor.cancel') }}
        </button>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="Boolean(jsonError) || !editorDraft"
          @click="formatJson"
        >
          {{ $t('commands.form.json_editor.format') }}
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="Boolean(jsonError) || !editorDraft"
          @click="applyEditor"
        >
          {{ $t('commands.form.json_editor.apply') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import axios from "@axios";

export default {
  components: {
    ValidationProvider,
  },
  props: {
    value: [String, Array, Object],
    multiple: Boolean | Number,
    config: Object,
    context: Object,
    multiline: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: Number,
      default: 2,
    },
    maxChars: {
      type: Number,
      default: 989,
    },
  },
  data() {
    return {
      data: [],
      model: null,
      maxChars: 5000,
      editorDraft: "",
      editorOpen: false,
      placeholderQuery: null,
      placeholderStart: null,
      activeSuggestionIndex: 0,
      placeholderPickerOpen: false,
      placeholderSearch: "",
      inlinePlaceholderQuery: null,
      inlinePlaceholderStart: null,
      inlineActiveSuggestionIndex: 0,
    };
  },
  created() {
    this.model = this.value;
  },
  async mounted() {
    try {
      const { data } = await axios.get("v1/command/placeholders");
      this.data = Array.isArray(data.data) ? data.data : [];
    } catch (error) {
      this.data = [];
    }
  },
  computed: {
    isWebhook() {
      return this.context?.model?.type === "webhook";
    },
    jsonError() {
      if (!this.editorDraft) return "";

      try {
        JSON.parse(this.editorDraft);
        return "";
      } catch (error) {
        return this.$t("commands.form.json_editor.invalid");
      }
    },
    editorCharsCount() {
      return Math.max(0, this.maxChars - this.editorDraft.length);
    },
    filteredPlaceholderSuggestions() {
      if (this.placeholderQuery === null) return [];

      const query = this.placeholderQuery.toLowerCase();
      return this.placeholders
        .filter(({ placeholder }) => placeholder.slice(1).toLowerCase().startsWith(query))
        .slice(0, 8);
    },
    filteredInlinePlaceholderSuggestions() {
      if (this.inlinePlaceholderQuery === null) return [];

      const query = this.inlinePlaceholderQuery.toLowerCase();
      return this.placeholders
        .filter(({ placeholder }) => placeholder.slice(1).toLowerCase().startsWith(query))
        .slice(0, 8);
    },
    placeholderGroups() {
      const groups = [
        { key: "alarm", label: this.$t("commands.form.message_template.categories.alarm"), placeholders: [] },
        { key: "location", label: this.$t("commands.form.message_template.categories.location"), placeholders: [] },
        { key: "camera", label: this.$t("commands.form.message_template.categories.camera"), placeholders: [] },
        { key: "media", label: this.$t("commands.form.message_template.categories.media"), placeholders: [] },
        { key: "other", label: this.$t("commands.form.message_template.categories.other"), placeholders: [] },
      ];

      this.placeholders.forEach((placeholder) => {
        const group = groups.find(({ key }) => key === this.placeholderGroupKey(placeholder.placeholder));
        group.placeholders.push(placeholder);
      });

      return groups.filter(({ placeholders }) => placeholders.length);
    },
    filteredPlaceholderGroups() {
      const query = this.placeholderSearch.trim().toLowerCase();
      if (!query) return this.placeholderGroups;

      return this.placeholderGroups
        .map((group) => ({
          ...group,
          placeholders: group.placeholders.filter(({ placeholder, description }) => (
            placeholder.toLowerCase().includes(query)
              || String(description || "").toLowerCase().includes(query)
          )),
        }))
        .filter(({ placeholders }) => placeholders.length);
    },
    chartsCount() {
      const chars = this.maxChars - (this.model ? this.model.length : 0);
      return chars <= 0 ? 0 : chars;
    },
    placeholders() {
      const apiPlaceholders = this.data.map((ph) => ({
        description: ["alarm_image", "alarm_image_url"].includes(ph.placeholder)
          ? this.$t(`commands.form.message_template.placeholders.${ph.placeholder}`)
          : ph.description,
        placeholder: `@${ph.placeholder}`,
      }));
      const additionalNames = [
        "alarm_image_url",
        "municipality",
        "region",
        "facility_format",
        "facility_determinant",
        "camera_name",
        "facility_comment",
      ];
      const existing = new Set(apiPlaceholders.map(({ placeholder }) => placeholder));
      const additionalPlaceholders = additionalNames
        .filter((name) => !existing.has(`@${name}`))
        .map((name) => ({
          description: this.$t(`commands.form.message_template.placeholders.${name}`),
          placeholder: `@${name}`,
        }));

      return [...apiPlaceholders, ...additionalPlaceholders];
    },
  },
  beforeDestroy() {
    this.restoreBodyScroll();
  },
  methods: {
    placeholderGroupKey(placeholder) {
      const name = placeholder.slice(1);
      if (name.startsWith("alarm_image")) return "media";
      if (name === "id_alarm" || name.startsWith("alarm_")) return "alarm";
      if (["unit_name", "municipality", "region"].includes(name) || name.startsWith("facility_")) {
        return "location";
      }
      if (name.startsWith("camera_")) return "camera";
      return "other";
    },
    togglePlaceholderPicker() {
      this.placeholderPickerOpen = !this.placeholderPickerOpen;
      this.placeholderSearch = "";
      this.hideInlinePlaceholderSuggestions();

      if (this.placeholderPickerOpen) {
        this.$nextTick(() => this.$refs.placeholderSearch?.focus());
      }
    },
    onTemplateInput(event) {
      if (this.isWebhook) {
        this.openEditor();
        return;
      }

      this.$emit("input", this.model);
      this.updateInlinePlaceholderSuggestions(event);
    },
    onTemplateInteraction(event) {
      if (this.isWebhook) {
        this.openEditor();
        return;
      }

      this.updateInlinePlaceholderSuggestions(event);
    },
    updateInlinePlaceholderSuggestions(event) {
      const textarea = event.target;
      const caret = textarea.selectionStart;
      const match = textarea.value.slice(0, caret).match(/@([\w]*)$/);

      if (!match) {
        this.hideInlinePlaceholderSuggestions();
        return;
      }

      this.inlinePlaceholderQuery = match[1];
      this.inlinePlaceholderStart = caret - match[0].length;
      this.inlineActiveSuggestionIndex = 0;
    },
    moveInlineSuggestion(event, direction) {
      const total = this.filteredInlinePlaceholderSuggestions.length;
      if (!total) return;

      event.preventDefault();
      this.inlineActiveSuggestionIndex = (
        this.inlineActiveSuggestionIndex + direction + total
      ) % total;
    },
    selectActiveInlineSuggestion(event) {
      if (!this.filteredInlinePlaceholderSuggestions.length) return;

      event.preventDefault();
      this.insertInlinePlaceholder(
        this.filteredInlinePlaceholderSuggestions[this.inlineActiveSuggestionIndex].placeholder,
        true
      );
    },
    insertInlinePlaceholder(placeholder, replaceQuery) {
      const textarea = this.$refs.templateInput;
      if (!textarea) return;

      const start = replaceQuery && this.inlinePlaceholderStart !== null
        ? this.inlinePlaceholderStart
        : textarea.selectionStart;
      const end = replaceQuery && this.inlinePlaceholderQuery !== null
        ? textarea.selectionStart
        : textarea.selectionEnd;
      const currentValue = textarea.value || "";
      const nextValue = `${currentValue.slice(0, start)}${placeholder}${currentValue.slice(end)}`;
      if (nextValue.length > this.maxChars) return;

      this.model = nextValue;
      this.$emit("input", this.model);
      this.placeholderPickerOpen = false;
      this.hideInlinePlaceholderSuggestions();

      this.$nextTick(() => {
        const nextCaret = start + placeholder.length;
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = nextCaret;
      });
    },
    hideInlinePlaceholderSuggestions() {
      this.inlinePlaceholderQuery = null;
      this.inlinePlaceholderStart = null;
      this.inlineActiveSuggestionIndex = 0;
    },
    openEditor() {
      if (!this.isWebhook || this.editorOpen) return;

      this.editorDraft = this.model || "";
      this.editorOpen = true;
      this.hidePlaceholderSuggestions();
      document.body.classList.add("json-editor-open");
      window.addEventListener("keydown", this.onWindowKeydown);
      this.$nextTick(() => this.$refs.jsonEditor?.focus());
    },
    closeEditor() {
      this.editorOpen = false;
      this.restoreBodyScroll();
    },
    applyEditor() {
      if (!this.editorDraft || this.jsonError) return;

      this.model = this.editorDraft;
      this.$emit("input", this.model);
      this.closeEditor();
    },
    formatJson() {
      if (!this.editorDraft || this.jsonError) return;

      const formatted = JSON.stringify(JSON.parse(this.editorDraft), null, 2);
      if (formatted.length <= this.maxChars) this.editorDraft = formatted;
    },
    updatePlaceholderSuggestions(event) {
      const textarea = event.target;
      const caret = textarea.selectionStart;
      const match = this.editorDraft.slice(0, caret).match(/@([\w]*)$/);

      if (!match) {
        this.hidePlaceholderSuggestions();
        return;
      }

      this.placeholderQuery = match[1];
      this.placeholderStart = caret - match[0].length;
      this.activeSuggestionIndex = 0;
    },
    moveSuggestion(event, direction) {
      const total = this.filteredPlaceholderSuggestions.length;
      if (!total) return;

      event.preventDefault();
      this.activeSuggestionIndex = (this.activeSuggestionIndex + direction + total) % total;
    },
    selectActiveSuggestion(event) {
      if (!this.filteredPlaceholderSuggestions.length) return;

      event.preventDefault();
      this.insertPlaceholder(
        this.filteredPlaceholderSuggestions[this.activeSuggestionIndex].placeholder
      );
    },
    insertPlaceholder(placeholder) {
      if (this.placeholderStart === null) return;

      const textarea = this.$refs.jsonEditor;
      const caret = textarea.selectionStart;
      const nextCaret = this.placeholderStart + placeholder.length;
      this.editorDraft = `${this.editorDraft.slice(0, this.placeholderStart)}${placeholder}${this.editorDraft.slice(caret)}`;
      this.hidePlaceholderSuggestions();
      this.$nextTick(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = nextCaret;
      });
    },
    hidePlaceholderSuggestions() {
      this.placeholderQuery = null;
      this.placeholderStart = null;
      this.activeSuggestionIndex = 0;
    },
    insertIndent(event) {
      if (this.editorDraft.length + 2 > this.maxChars) return;

      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      this.editorDraft = `${this.editorDraft.slice(0, start)}  ${this.editorDraft.slice(end)}`;
      this.$nextTick(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2;
      });
    },
    onWindowKeydown(event) {
      if (event.key === "Escape") this.closeEditor();
    },
    restoreBodyScroll() {
      document.body.classList.remove("json-editor-open");
      window.removeEventListener("keydown", this.onWindowKeydown);
    },
  },
};
</script>

<style lang="scss">
body.json-editor-open {
  overflow: hidden;
}
</style>

<style scoped lang="scss">
.message-template__input {
  resize: vertical;
}

.message-template__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}

.message-template__placeholder-button {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
}

.message-template__field {
  position: relative;
}

.message-template__suggestions,
.message-template__placeholder-picker {
  z-index: 3;
  border: 1px solid #ced4da;
  border-radius: 0.375rem;
  background: #fff;
  box-shadow: 0 0.5rem 1.25rem rgba(34, 41, 47, 0.16);
}

.message-template__suggestions {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  width: min(30rem, 100%);
  max-height: 16rem;
  overflow-y: auto;
}

.message-template__suggestion,
.message-template__placeholder-option {
  display: flex;
  width: 100%;
  border: 0;
  background: transparent;
  color: #3b4252;
  text-align: left;
}

.message-template__suggestion {
  flex-direction: column;
  gap: 0.125rem;
  padding: 0.5rem 0.75rem;

  small {
    color: #6c757d;
  }
}

.message-template__suggestion--active,
.message-template__suggestion:hover,
.message-template__placeholder-option:hover {
  background: #f1f5f9;
}

.message-template__placeholder-picker {
  max-height: 24rem;
  margin-top: 0.5rem;
  padding: 0.75rem;
  overflow-y: auto;
}

.message-template__placeholder-group + .message-template__placeholder-group {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e9ecef;
}

.message-template__placeholder-group-title {
  margin: 0 0 0.375rem;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.message-template__placeholder-option {
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;

  code {
    flex: 0 0 11rem;
    color: #2f5f8f;
    font-size: 0.8125rem;
  }

  span {
    color: #6c757d;
    font-size: 0.8125rem;
  }
}

@media (max-width: 575.98px) {
  .message-template__label-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .message-template__placeholder-option {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.125rem;

    code {
      flex-basis: auto;
    }
  }
}

.message-template__input--webhook {
  min-height: 10rem;
  cursor: pointer;
  font-family: Consolas, "Courier New", monospace;
  white-space: pre;
}

.webhook-editor {
  position: fixed;
  z-index: 10000;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  background: #f5f6f8;
}

.webhook-editor__header,
.webhook-editor__footer {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border: 1px solid #dee2e6;
  background: #fff;
}

.webhook-editor__header {
  justify-content: space-between;
  border-radius: 0.5rem 0.5rem 0 0;
}

.webhook-editor__title {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
}

.webhook-editor__close {
  padding: 0 0.5rem;
  border: 0;
  background: transparent;
  color: #6c757d;
  font-size: 2rem;
  line-height: 1;
}

.webhook-editor__body {
  position: relative;
  display: flex;
  flex: 1;
  min-height: 0;
  border-right: 1px solid #dee2e6;
  border-left: 1px solid #dee2e6;
}

.webhook-editor__suggestions {
  position: absolute;
  z-index: 2;
  top: 1rem;
  left: 1rem;
  width: min(30rem, calc(100% - 2rem));
  max-height: 22rem;
  overflow-y: auto;
  border: 1px solid #495867;
  border-radius: 0.375rem;
  background: #263442;
  box-shadow: 0 0.5rem 1.25rem rgba(0, 0, 0, 0.35);
}

.webhook-editor__suggestion {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: transparent;
  color: #e8edf2;
  text-align: left;

  &:last-child {
    border-bottom: 0;
  }

  small {
    margin-top: 0.15rem;
    color: #b9c4cf;
  }
}

.webhook-editor__suggestion--active,
.webhook-editor__suggestion:hover {
  background: #365a78;
}

.webhook-editor__textarea {
  flex: 1;
  width: 100%;
  padding: 1.25rem;
  resize: none;
  border: 0;
  outline: 0;
  background: #17212b;
  color: #e8edf2;
  font-family: Consolas, "Courier New", monospace;
  font-size: 1rem;
  line-height: 1.6;
  tab-size: 2;
}

.webhook-editor__footer {
  justify-content: flex-end;
  gap: 0.75rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

.webhook-editor__counter {
  margin-right: auto;
  color: #6c757d;
}

@media (max-width: 575.98px) {
  .webhook-editor {
    padding: 0;
  }

  .webhook-editor__header,
  .webhook-editor__footer {
    border-radius: 0;
  }

  .webhook-editor__footer {
    flex-wrap: wrap;
  }

  .webhook-editor__counter {
    width: 100%;
  }
}
</style>
